import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, CaptionText, BodyText, Link } from '@paypalcorp/pp-react';
import {
  SingleAmount,
  Title,
  CancelAndReturnUrl,
  LogoImage,
  CampaignImage,
} from '@paypalcorp/donate-react';
import { MessageFormat } from '@paypalcorp/worldready';
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react';
import MailingAddress from '../../shared/preview/MailingAddress';
import { LOGO_TARGET_PROFILE } from '../../reducers/reducerCampaigns';
import ButtonFactoryPreviewPPLogoHeader from '../../shared/preview/ButtonFactoryPreviewPPLogoHeader';
import { getPPCCStatus } from '../../../utility/helper';
import { MERCHANT_PPCC_CONFIRMED_STATUS } from '../../../utility/constants';
import DonorPaymentInfoSkeleton from '../../shared/preview/DonorPaymentInfoSkeleton';
import PPCC from '../../shared/ppcc';
import withTheme from '../../componentsV3/withTheme';
import { normalizeNumber } from '../../utilsV3/amountUtils';

function CampaignGuestFormPreview({ isMobile }) {
  const formValues = useSelector((state) => state.form?.campaigns?.values);
  const html = document.getElementsByTagName('html')[0];
  const directionality = html.getAttribute('dir');
  const {
    amountType,
    amountValue,
    currencyCode,
    merchantAmount1,
    logoUrl,
    businessProfileLogoUrl,
    logoTarget,
    noLogoImage,
    noShipping,
    noCancelReturn,
    receiverName,
    bannerImageUrl,
    eventTitle,
  } = formValues;

  const worldReady = useContext(WorldReadyContext);

  const contentCancelAndReturn = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.campaignDesktopPreview.cancelAndReturn',
  }).format({ orgName: receiverName });

  const holderText = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.campaignImage.holderText',
  }).format();

  const isPresetAmount = amountType === 'merchant_recommended';
  const showCancelOrReturn = noCancelReturn === 'no';
  const showPPCCIcon = getPPCCStatus() === MERCHANT_PPCC_CONFIRMED_STATUS;

  return (
    <>
      {!isMobile && <ButtonFactoryPreviewPPLogoHeader />}
      <div
        className={
          isMobile
            ? 'campaign-common-preview-container campaigns'
            : 'campaign-guest-page-preview'
        }
        data-testid="campaignGuestPagePreview"
      >
        <CampaignImage
          src={bannerImageUrl}
          className="img-container"
          style={isMobile ? { height: 'auto' } : {}}
        />

        <div
          className="container"
          data-testid="campaignGuestPagePreviewContainer"
        >
          {noLogoImage === 'no' && (
            <LogoImage
              src={
                logoTarget === LOGO_TARGET_PROFILE
                  ? businessProfileLogoUrl
                  : logoUrl
              }
              content={{ holderText: holderText }}
            />
          )}
          <CaptionText
            className={`text-center ${
              noLogoImage === 'no' ? 'margin-top-24' : ''
            }`}
          >
            <Message id="buttonsV3.buttonOverview.phonePreview.donateTo" />
          </CaptionText>
          <BodyText className="text-center" size="sm">
            {receiverName}
            {showPPCCIcon && (
              <span className="preview-verified-account-icon">
                <PPCC />
              </span>
            )}
          </BodyText>
          {eventTitle && (
            <div className="text-center margin-bottom-12">
              <Title size="sm" title={eventTitle} />
            </div>
          )}
          <div className={isMobile ? 'campaign-guest-form' : null}>
            <SingleAmount
              amount={
                isPresetAmount
                  ? normalizeNumber(merchantAmount1).toString()
                  : normalizeNumber(amountValue).toString()
              }
              currencyCode={currencyCode}
              isEditable={false}
              isPreview={true}
              directionality={directionality}
            />
            <DonorPaymentInfoSkeleton />
            {noShipping === 'no' && <MailingAddress />}
            <CaptionText className="text-center margin-top-24">
              <Message
                id="buttonsV3.preview.wr.terms"
                lineBreak={<br />}
                link={(innerMessage) => (
                  <Link href="/" size="sm">
                    {innerMessage}
                  </Link>
                )}
              />
            </CaptionText>
            <Button className="campaign-guest-preview-btn" disabled>
              <Message id="buttonsV3.preview.preview.donateNow" />
            </Button>
          </div>
        </div>
        {isMobile && showCancelOrReturn && (
          <div className="campaign-guest-cancel-preview">
            <CancelAndReturnUrl content={contentCancelAndReturn} />
          </div>
        )}
      </div>

      {!isMobile && showCancelOrReturn && (
        <div className="campaign-guest-cancel-preview">
          <CancelAndReturnUrl content={contentCancelAndReturn} />
        </div>
      )}
    </>
  );
}

CampaignGuestFormPreview.propTypes = {
  campaignImageUrl: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default withTheme(CampaignGuestFormPreview);
